"use client";

import React, { useState, forwardRef, useEffect } from "react";
import PhoneInput from "react-phone-number-input";
import { CurrencyInput } from "react-currency-mask";
import "react-phone-number-input/style.css";

import { Eye, EyeOff, Search } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { MessageError } from "../common";

type InputProps = React.ComponentProps<"input"> & {
  isInvalid?: boolean | string | null;
  isValid?: boolean | string | null;
  messageError?: string;
  showError?: boolean;
  inputPhone?: boolean;
  inputMoney?: boolean;
  inputPercentage?: boolean;
  defaultValue?: string;
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      isInvalid,
      isValid,
      messageError,
      showError = true,
      inputPhone,
      inputMoney,
      inputPercentage,
      onChange,
      value,
      defaultValue,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [internalValue, setInternalValue] = useState<string>("");

    // Sincroniza o valor externo com o interno
    useEffect(() => {
      if (value !== undefined) {
        if (inputPercentage) {
          // Remove % se existir e converte para número
          const numValue = typeof value === 'string'
            ? value.replace('%', '').trim()
            : String(value);
          setInternalValue(numValue);
        } else {
          setInternalValue(String(value));
        }
      }
    }, [value, inputPercentage]);

    const togglePasswordVisibility = () => setShowPassword(!showPassword);

    const handlePhoneChange = (phoneValue: string | undefined) => {
      const maxLength = 15;
      if (phoneValue && phoneValue.length > maxLength) return;
      setInternalValue(phoneValue || "");
      if (onChange) {
        const event = {
          target: { name: props.name, value: phoneValue || "" },
        };
        onChange(event as React.ChangeEvent<HTMLInputElement>);
      }
    };

    const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;

      // Remove o símbolo % para processamento
      let valueWithoutPercent = rawValue.replace('%', '');

      // Permite apenas números, ponto e vírgula
      valueWithoutPercent = valueWithoutPercent.replace(/[^\d.,]/g, '');

      // Substitui vírgula por ponto para padrão numérico
      const numericString = valueWithoutPercent.replace(',', '.');

      // Converte para número para validação
      const numValue = parseFloat(numericString);

      // Se não for um número válido ou estiver vazio, permite a digitação
      if (isNaN(numValue)) {
        setInternalValue(valueWithoutPercent);
        return;
      }

      // Valida o valor máximo (100)
      if (numValue > 100) {
        // Define o valor máximo como 100
        setInternalValue("100");
        if (onChange) {
          const event = {
            target: {
              name: props.name,
              value: "100"
            },
          };
          onChange(event as React.ChangeEvent<HTMLInputElement>);
        }
        return;
      }

      // Permite valores decimais, mas limita a 2 casas
      let finalValue = numericString;
      if (numValue === 100) {
        // Se for exatamente 100, remove decimais
        finalValue = "100";
      } else {
        // Limita a 2 casas decimais
        const decimalParts = numericString.split('.');
        if (decimalParts.length > 1 && decimalParts[1].length > 2) {
          finalValue = `${decimalParts[0]}.${decimalParts[1].slice(0, 2)}`;
        }
      }

      setInternalValue(finalValue);

      if (onChange) {
        const event = {
          target: {
            name: props.name,
            value: finalValue
          },
        };
        onChange(event as React.ChangeEvent<HTMLInputElement>);
      }
    };

    const handleBlurPercentage = () => {
      // Formata ao perder o foco
      if (internalValue === "") {
        if (onChange) {
          const event = {
            target: {
              name: props.name,
              value: ""
            },
          };
          onChange(event as React.ChangeEvent<HTMLInputElement>);
        }
        return;
      }

      const numValue = parseFloat(internalValue);

      if (!isNaN(numValue)) {
        // Garante que não ultrapasse 100
        const clampedValue = Math.min(numValue, 100);

        // Formata para 2 casas decimais se necessário
        let formattedValue = clampedValue.toString();
        if (clampedValue % 1 !== 0) {
          formattedValue = clampedValue.toFixed(2);
        }

        setInternalValue(formattedValue);

        if (onChange) {
          const event = {
            target: {
              name: props.name,
              value: formattedValue
            },
          };
          onChange(event as React.ChangeEvent<HTMLInputElement>);
        }
      }
    };

    const getDisplayPercentageValue = () => {
      if (internalValue === "") return "";

      const numValue = parseFloat(internalValue);
      if (isNaN(numValue)) return internalValue;

      // Se estiver editando, mostra sem %, senão mostra com %
      const hasFocus = document.activeElement === document.querySelector(`[name="${props.name}"]`);

      if (hasFocus) {
        return internalValue;
      } else {
        // Formata com 2 casas decimais se for decimal
        let displayValue = internalValue;
        if (numValue % 1 !== 0) {
          const decimalParts = internalValue.split('.');
          if (decimalParts.length > 1 && decimalParts[1].length === 1) {
            displayValue = `${decimalParts[0]}.${decimalParts[1]}0`;
          }
        }
        return `${displayValue}%`;
      }
    };

    // Manipulador de foco para remover % durante a edição
    const handleFocusPercentage = () => {
      // Remove o % se existir no foco
      if (internalValue.endsWith('%')) {
        setInternalValue(internalValue.slice(0, -1));
      }
    };

    const inputClasses = cn(
      "flex h-10 w-full rounded-lg border border-app-border bg-app-surface px-4 py-2 text-base text-app-fg-strong ring-offset-background placeholder:text-app-fg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/35 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
      type === "password" && "pr-10",
      type === "search" && "pl-10",
      className,
      {
        "is-invalid": isInvalid,
        "is-valid": isValid,
      }
    );

    return (
      <div className="space-y-1">
        {inputMoney ? (
          <CurrencyInput
            onChangeValue={(event, originalValue) => {
              const e = {
                target: {
                  name: event.currentTarget.name,
                  value: originalValue,
                },
              };
              setInternalValue(String(originalValue));
              onChange?.(e as any);
            }}
            defaultValue={defaultValue}
            InputElement={<input className={inputClasses} {...props} />}
          />
        ) : (
          <>
            {inputPhone ? (
              <PhoneInput
                defaultCountry="BR"
                value={internalValue as string}
                onChange={handlePhoneChange}
                className={inputClasses}
                {...props}
              />
            ) : inputPercentage ? (
              <input
                type="text"
                className={inputClasses}
                value={getDisplayPercentageValue()}
                onChange={handlePercentageChange}
                onFocus={handleFocusPercentage}
                onBlur={handleBlurPercentage}
                onKeyDown={(e) => {
                  // Permite usar as teclas de navegação mesmo quando o valor é 100
                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    const currentValue = parseFloat(internalValue) || 0;
                    let newValue = currentValue;

                    if (e.key === 'ArrowUp') {
                      newValue = Math.min(currentValue + 1, 100);
                    } else if (e.key === 'ArrowDown') {
                      newValue = Math.max(currentValue - 1, 0);
                    }

                    setInternalValue(newValue.toString());
                    if (onChange) {
                      const event = {
                        target: {
                          name: props.name,
                          value: newValue.toString()
                        },
                      };
                      onChange(event as React.ChangeEvent<HTMLInputElement>);
                    }
                  }
                }}
                ref={ref}
                {...props}
                max={100} // Atributo HTML para validação nativa
              />
            ) : (
              <div className="relative">
                {type === "search" && (
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <Search className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
                <input
                  type={type === "password" && showPassword ? "text" : type}
                  className={inputClasses}
                  value={value}
                  onChange={onChange}
                  ref={ref}
                  {...props}
                />
                {type === "password" && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    data-error={isInvalid ? true : false}
                    data-isValid={isValid ? true : false}
                    className="absolute data-[error=true]:right-7 data-[isValid=true]:right-7 right-0 top-1/2 transform -translate-y-1/2 h-full px-3 py-2 hover:bg-transparent"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="sr-only">
                      {showPassword ? "Hide password" : "Show password"}
                    </span>
                  </Button>
                )}
              </div>
            )}
          </>
        )}

        {showError && (
          <MessageError isInvalid={isInvalid} messageError={messageError} />
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input };